import gql from "graphql-tag";
import type { ApolloClient } from "@apollo/client";

import type {
  AcknowledgeRecurringDebitsMutation,
  AcknowledgeRecurringDebitsMutationVariables,
  GetRecurringDebitsAcknowledgmentQuery,
  GetRecurringDebitsAcknowledgmentQueryVariables,
  ActivatePayrollIntegrationMutation,
  ActivatePayrollIntegrationMutationVariables,
  AddEmployerGroupMutation,
  AddEmployerGroupMutationVariables,
  ApproveContributionChangesMutation,
  ApproveContributionChangesMutationVariables,
  ApproveEmployerLinkRequestsMutation,
  ApproveEmployerLinkRequestsMutationVariables,
  ApproveRosterMutation,
  ApproveRosterMutationVariables,
  ChangeRoleMutation,
  ChangeRoleMutationVariables,
  CompanyByUserQuery,
  CompanyByUserQueryVariables,
  CompanyTermsDateQuery,
  CompanyTermsDateQueryVariables,
  CreateSetupIntentMutation,
  CreateSetupIntentMutationVariables,
  CurrentPayrollsQuery,
  CurrentPayrollsQueryVariables,
  DeleteEmployerGroupMutation,
  DeleteEmployerGroupMutationVariables,
  DeleteEmployerLinksMutation,
  DeleteEmployerLinksMutationVariables,
  EmployerAccountListQuery,
  EmployerAccountListQueryVariables,
  GetAllAgentLinksForCompanyQuery,
  GetAllAgentLinksForCompanyQueryVariables,
  GetAllEmployeeInvitesForCompanyQuery,
  GetAllEmployeeInvitesForCompanyQueryVariables,
  GetBeneficialOwnersQuery,
  GetBeneficialOwnersQueryVariables,
  GetBillingPortalUrlQuery,
  GetBillingPortalUrlQueryVariables,
  GetCompanyAgreementsQuery,
  GetCompanyAgreementsQueryVariables,
  GetCompanyQuery,
  GetCompanyQueryVariables,
  GetEmployeeDocumentUploadJobsQuery,
  GetEmployeeDocumentUploadJobsQueryVariables,
  GetEmployeeRosterQuery,
  GetEmployeeRosterQueryVariables,
  GetEmployerContributionAbilityQuery,
  GetEmployerContributionAbilityQueryVariables,
  GetEmployerGroupQuery,
  GetEmployerGroupQueryVariables,
  GetEmployerGroupsQuery,
  GetEmployerGroupsQueryVariables,
  GetEmployerLinkRequestsQuery,
  GetEmployerLinkRequestsQueryVariables,
  GetEmployerOrderQuery,
  GetEmployerOrderQueryVariables,
  GetEmployerRequiredUpdatesQuery,
  GetEmployerRequiredUpdatesQueryVariables,
  GetEmployerWalletDetailsQuery,
  GetEmployerWalletDetailsQueryVariables,
  GetLinkedCompaniesQuery,
  GetLinkedCompaniesQueryVariables,
  GetLinkedEmployeesQuery,
  GetLinkedEmployeesQueryVariables,
  GetPaginatedEmployeesQuery,
  GetPaginatedEmployeesQueryVariables,
  GetPaginatedInvitesQuery,
  GetPaginatedInvitesQueryVariables,
  GetPayPeriodExamplesQuery,
  GetPayPeriodExamplesQueryVariables,
  GetPayrollGroupContributionAbilityQuery,
  GetPayrollGroupContributionAbilityQueryVariables,
  GetPayrollIntegrationStatusQuery,
  GetPayrollIntegrationStatusQueryVariables,
  GetProgressiveOnboardingQuery,
  GetProgressiveOnboardingQueryVariables,
  GetSetupIntentQuery,
  GetSetupIntentQueryVariables,
  GetPaymentRequirementQuery,
  GetPaymentRequirementQueryVariables,
  GetTakeRateEmployeesForCompanyQuery,
  GetTakeRateEmployeesForCompanyQueryVariables,
  GetUkgConfigQuery,
  GetUkgConfigQueryVariables,
  GetUserProfilesForGroupQuery,
  GetUserProfilesForGroupQueryVariables,
  HydrateDashboardQuery,
  HydrateDashboardQueryVariables,
  InviteEmployerAgentMutation,
  InviteEmployerAgentMutationVariables,
  InviteUsersMutation,
  InviteUsersMutationVariables,
  LockAgentMutation,
  LockAgentMutationVariables,
  PostEmployerAgentMutation,
  PostEmployerAgentMutationVariables,
  ProcessPayrollMutation,
  ProcessPayrollMutationVariables,
  ProcessRosterMutation,
  ProcessRosterMutationVariables,
  ReferCompanyMutation,
  ReferCompanyMutationVariables,
  RegisterCompanyMutation,
  RegisterCompanyMutationVariables,
  RejectContributionChangesMutation,
  RejectContributionChangesMutationVariables,
  RejectEmployerLinkRequestsMutation,
  RejectEmployerLinkRequestsMutationVariables,
  ResendAgentInvitationMutation,
  ResendAgentInvitationMutationVariables,
  ResendInvitationMutation,
  ResendInvitationMutationVariables,
  ResendInvitationsMutation,
  ResendInvitationsMutationVariables,
  ResyncEmployeeRosterMutation,
  ResyncEmployeeRosterMutationVariables,
  RevokeInvitationMutation,
  RevokeInvitationMutationVariables,
  SetEmployerSessionMutation,
  SetEmployerSessionMutationVariables,
  SetInvoicePaymentMethodMutation,
  SetInvoicePaymentMethodMutationVariables,
  SkipBillingMutation,
  SkipBillingMutationVariables,
  StageUserInvitesMutation,
  StageUserInvitesMutationVariables,
  SubmitUkgConfigMutation,
  SubmitUkgConfigMutationVariables,
  UnlockAgentMutation,
  UnlockAgentMutationVariables,
  UpdateEmployerGroupMutation,
  UpdateEmployerGroupMutationVariables,
  UpdateProgressiveOnboardingMutation,
  UpdateProgressiveOnboardingMutationVariables,
  UpdateRequiredOrderFormMutation,
  UpdateRequiredOrderFormMutationVariables,
  UploadEmployeeDocumentMutation,
  UploadEmployeeDocumentMutationVariables,
  UploadRosterMutation,
  UploadRosterMutationVariables,
  ValidateSelfServeMutation,
  ValidateSelfServeMutationVariables,
} from "types/graphql";

import { get } from "lodash";

function register(
  client: ApolloClient<object>,
  {
    company: employer,
    organizationId,
    needsStatePlan,
  }: {
    company: Omit<
      RegisterCompanyMutationVariables,
      "organizationId" | "needsStatePlan"
    >;
    organizationId: RegisterCompanyMutationVariables["organizationId"];
    needsStatePlan: RegisterCompanyMutationVariables["needsStatePlan"];
  }
): Promise<RegisterCompanyMutation["addCompany"] | null> {
  return new Promise((resolve, reject) => {
    const ADD_COMPANY = gql`
      mutation RegisterCompany(
        $name: String!
        $dba: String
        $ein: String
        $type: String
        $naicsCode: String!
        $size: String!
        $url: String
        $address1: String!
        $address2: String
        $city: String!
        $state: String!
        $postalCode: String!
        $phone: String!
        $phoneExt: String
        $fax: String
        $referralCode: String
        $organizationId: ID
        $payrollProvider: String!
        $planCode: String
        $needsStatePlan: Boolean!
        $registrationDate: String
        $signupCode: String
      ) {
        addCompany(
          name: $name
          dba: $dba
          ein: $ein
          type: $type
          naicsCode: $naicsCode
          size: $size
          url: $url
          address1: $address1
          address2: $address2
          city: $city
          state: $state
          postalCode: $postalCode
          phone: $phone
          phoneExt: $phoneExt
          fax: $fax
          referralCode: $referralCode
          organizationId: $organizationId
          payrollProvider: $payrollProvider
          needsStatePlan: $needsStatePlan
          planCode: $planCode
          registrationDate: $registrationDate
          signupCode: $signupCode
        ) {
          company {
            id
            name
            dba
            type
            naicsCode
            size
            url
            referralCode
            planCode
            limits {
              key
              value
            }
            address {
              id
              address1
              address2
              city
              state
              postalCode
              phone
              phoneExt
              fax
            }
          }
          error
        }
      }
    `;
    const result = client.mutate<
      RegisterCompanyMutation,
      RegisterCompanyMutationVariables
    >({
      mutation: ADD_COMPANY,
      variables: {
        name: employer.name,
        dba: employer.dba,
        ein: employer.ein,
        type: employer.type,
        naicsCode: employer.naicsCode,
        size: employer.size,
        url: employer.url,
        address1: employer.address1,
        address2: employer.address2,
        city: employer.city,
        state: employer.state,
        postalCode: employer.postalCode,
        phone: employer.phone,
        phoneExt: employer.phoneExt,
        fax: employer.fax,
        payrollProvider: employer.payrollProvider,
        organizationId,
        needsStatePlan,
        planCode: employer.planCode,
        registrationDate: employer.registrationDate,
        signupCode: employer.signupCode,
      },
    });
    result.then(
      (response) => {
        // pre-existing: the mutation's error field lives under
        // data.addCompany.error — this top-level read has always been
        // undefined, so the reject branch is dead (recorded in the doc)
        if ((response.data as { error?: unknown } | null)?.error) {
          // eslint-disable-next-line @typescript-eslint/prefer-promise-reject-errors -- pre-existing contract: rejects the in-band error value
          reject((response.data as { error?: unknown } | null)?.error);
        } else {
          resolve(response.data?.addCompany ?? null);
        }
      },
      (error: Error) => {
        console.error(error);
        reject(error);
      }
    );
  });
}

export function submitEmployerAgents(
  client: ApolloClient<object>,
  employerAgent: PostEmployerAgentMutationVariables
): Promise<PostEmployerAgentMutation> {
  return new Promise((resolve, reject) => {
    const REGISTER_AGENT = gql`
      mutation postEmployerAgent(
        $firstName: String!
        $lastName: String!
        $email: String!
        $role: String!
      ) {
        postEmployerAgent(
          firstName: $firstName
          lastName: $lastName
          email: $email
          role: $role
        ) {
          error
        }
      }
    `;
    const result = client.mutate<
      PostEmployerAgentMutation,
      PostEmployerAgentMutationVariables
    >({
      mutation: REGISTER_AGENT,
      variables: {
        firstName: employerAgent.firstName,
        lastName: employerAgent.lastName,
        email: employerAgent.email,
        role: employerAgent.role,
      },
    });
    result.then(
      (response) => {
        const inBandError = response.data?.postEmployerAgent?.error;
        if (inBandError) {
          // eslint-disable-next-line @typescript-eslint/prefer-promise-reject-errors -- string rejections surface verbatim through wrapError
          reject(inBandError);
        } else if (response.data) {
          resolve(response.data);
        } else {
          // eslint-disable-next-line @typescript-eslint/prefer-promise-reject-errors -- pre-existing contract: rejects the raw Apollo response
          reject(response);
        }
      },
      (error: Error) => {
        reject(error);
      }
    );
  });
}

// get company details
function getCompany(
  client: ApolloClient<object>
): Promise<GetCompanyQuery["company"]> {
  return new Promise((resolve, reject) => {
    const GET_COMPANY = gql`
      query getCompany {
        company {
          id
          name
          dba
          ein
          type
          naicsCode
          size
          url
          planId
          enabledProductFeatures
          planCode
          payrollProvider
          billingType
          limits {
            key
            value
          }
          address {
            id
            address1
            address2
            city
            state
            postalCode
            phone
            phoneExt
            fax
          }
          planTypes {
            id
            description
            name
          }
          payrollIntegrationStatus
          hasRunFirstPayroll
          referralId
          registrationDate
          referralPlans {
            min
            max
            planId
            referralCredit
            refereeCredit
          }
          progressiveOnboardingState
        }
      }
    `;
    const result = client.query<GetCompanyQuery, GetCompanyQueryVariables>({
      query: GET_COMPANY,
      variables: {},
    });
    result.then(
      (data) => {
        resolve(data.data.company);
      },
      (error: Error) => {
        reject(error);
      }
    );
  });
}

function findEmployerByUser(
  client: ApolloClient<object>
): Promise<CompanyByUserQuery["companyByUser"]["company"]> {
  return new Promise((resolve, reject) => {
    const COMPANY_BY_USER = gql`
      query CompanyByUser {
        companyByUser {
          company {
            id
            name
            dba
            ein
            type
            naicsCode
            size
            url
            address {
              id
              address1
              address2
              city
              state
              postalCode
              phone
              phoneExt
              fax
            }
          }
          error
        }
      }
    `;
    const result = client.query<
      CompanyByUserQuery,
      CompanyByUserQueryVariables
    >({
      query: COMPANY_BY_USER,
    });
    result.then(
      (response) => {
        if (response.data.companyByUser.error) {
          // eslint-disable-next-line @typescript-eslint/prefer-promise-reject-errors -- pre-existing contract: rejects the in-band error value
          reject(response.data.companyByUser.error);
        } else {
          resolve(response.data.companyByUser.company);
        }
      },
      (error: Error) => {
        console.error(error);
        reject(error);
      }
    );
  });
}

// get Employer Accounts
function getAllEmployerAccounts(
  client: ApolloClient<object>
): Promise<EmployerAccountListQuery["employerAccountList"]> {
  return new Promise((resolve, reject) => {
    const GET_ALL_ACCOUNTS = gql`
      query employerAccountList {
        employerAccountList {
          accountId
          id
          accountType
          accountSubtype
          userId
          bankName
          bankAlias
          linkStatus
          state
          loginRequired
        }
      }
    `;
    const result = client.query<
      EmployerAccountListQuery,
      EmployerAccountListQueryVariables
    >({
      query: GET_ALL_ACCOUNTS,
      variables: {},
      fetchPolicy: "network-only",
    });
    result.then(
      (data) => {
        resolve(data.data.employerAccountList);
      },
      (error: Error) => {
        reject(error);
      }
    );
  });
}

// get Employer Groups
function getAllEmployerGroups(
  client: ApolloClient<object>
): Promise<GetEmployerGroupsQuery["getEmployerGroups"]> {
  return new Promise((resolve, reject) => {
    const GET_ALL_GROUPS = gql`
      query getEmployerGroups {
        getEmployerGroups {
          id
          name
          payrollFrequency
          useUniqueInviteCodes
          associatedBankAccount {
            id
            bankName
          }
          associatedLinkRequests {
            status
            id
          }
          associatedSchedule {
            start
            end
            payPeriodEnd
            payrollProcessingDate
            onOrAfterPayrollProcessingDate
            status
            gracePeriodEndDate
          }
          schedule {
            anchorPayDate
            payrollProcessingOffset
            payrollFrequency
            dayOne
            dayTwo
            displayAnchorPayDate
            displayDayOne
            displayDayTwo
          }
          planType
          companyGroupIndex
        }
      }
    `;
    const result = client.query<
      GetEmployerGroupsQuery,
      GetEmployerGroupsQueryVariables
    >({
      query: GET_ALL_GROUPS,
      variables: {},
      fetchPolicy: "network-only",
    });
    result.then(
      (data) => {
        resolve(data.data.getEmployerGroups);
      },
      (error: Error) => {
        reject(error);
      }
    );
  });
}

function getEmployerGroup(
  client: ApolloClient<object>,
  groupId: string
): Promise<GetEmployerGroupQuery["getEmployerGroup"]> {
  return new Promise((resolve, reject) => {
    const GET_ALL_GROUPS = gql`
      query getEmployerGroup($groupId: ID!) {
        getEmployerGroup(groupId: $groupId) {
          id
          name
          payrollFrequency
          useUniqueInviteCodes
          associatedBankAccount {
            id
            bankName
          }
          associatedLinkRequests {
            status
            id
          }
          planType
          companyGroupIndex
        }
      }
    `;
    const result = client.query<
      GetEmployerGroupQuery,
      GetEmployerGroupQueryVariables
    >({
      query: GET_ALL_GROUPS,
      variables: { groupId },
      fetchPolicy: "network-only",
    });
    result.then(
      (data) => {
        resolve(data.data.getEmployerGroup);
      },
      (error: Error) => {
        reject(error);
      }
    );
  });
}

// Add Employer Group
function addEmployerGroup(
  client: ApolloClient<object>,
  companyId: string,
  group: Omit<AddEmployerGroupMutationVariables, "userBankId"> & {
    associatedBankAccount: AddEmployerGroupMutationVariables["userBankId"];
  }
): Promise<AddEmployerGroupMutation["addEmployerGroup"] | null> {
  return new Promise((resolve, reject) => {
    const ADD_EMPLOYER_GROUP = gql`
      mutation addEmployerGroup(
        $name: String!
        $companyId: String
        $payrollFrequency: Frequency!
        $payrollProcessingOffset: Int!
        $dayOne: Int
        $dayTwo: Int
        $anchorPayDate: Date!
        $userBankId: ID!
        $planType: PlanType!
        $useUniqueInviteCodes: Boolean!
        $displayDayOne: Int
        $displayDayTwo: Int
        $displayAnchorPayDate: Date
      ) {
        addEmployerGroup(
          input: {
            name: $name
            payrollFrequency: $payrollFrequency
            payrollProcessingOffset: $payrollProcessingOffset
            dayOne: $dayOne
            dayTwo: $dayTwo
            anchorPayDate: $anchorPayDate
            userBankId: $userBankId
            planType: $planType
            useUniqueInviteCodes: $useUniqueInviteCodes
            displayDayOne: $displayDayOne
            displayDayTwo: $displayDayTwo
            displayAnchorPayDate: $displayAnchorPayDate
            companyId: $companyId
          }
        ) {
          id
          name
          payrollFrequency
          useUniqueInviteCodes
          associatedBankAccount {
            id
            bankName
          }
        }
      }
    `;

    const result = client.mutate<
      AddEmployerGroupMutation,
      AddEmployerGroupMutationVariables
    >({
      mutation: ADD_EMPLOYER_GROUP,
      variables: {
        name: group.name,
        payrollFrequency: group.payrollFrequency,
        payrollProcessingOffset: group.payrollProcessingOffset,
        anchorPayDate: group.anchorPayDate,
        userBankId: group.associatedBankAccount,
        dayOne: group.dayOne,
        dayTwo: group.dayTwo,
        planType: group.planType,
        useUniqueInviteCodes: group.useUniqueInviteCodes,
        displayAnchorPayDate: group.displayAnchorPayDate,
        displayDayOne: group.displayDayOne,
        displayDayTwo: group.displayDayTwo,
        companyId,
      },
    });
    result.then(
      (data) => {
        resolve(data.data?.addEmployerGroup ?? null);
      },
      (error: Error) => {
        reject(error);
      }
    );
  });
}

// Update Employer Group
function updateEmployerGroup(
  client: ApolloClient<object>,
  group: Omit<UpdateEmployerGroupMutationVariables, "userBankId"> & {
    associatedBankAccount: UpdateEmployerGroupMutationVariables["userBankId"];
  },
  companyId: string
): Promise<UpdateEmployerGroupMutation["updateEmployerGroup"] | null> {
  return new Promise((resolve, reject) => {
    const UPDATE_EMPLOYER_GROUP = gql`
      mutation updateEmployerGroup(
        $id: ID!
        $companyId: ID
        $name: String!
        $userBankId: ID!
        $useUniqueInviteCodes: Boolean!
        $displayDayOne: Int
        $displayDayTwo: Int
        $displayAnchorPayDate: Date
      ) {
        updateEmployerGroup(
          id: $id
          name: $name
          userBankId: $userBankId
          useUniqueInviteCodes: $useUniqueInviteCodes
          displayDayOne: $displayDayOne
          displayDayTwo: $displayDayTwo
          displayAnchorPayDate: $displayAnchorPayDate
          companyId: $companyId
        ) {
          id
          name
          payrollFrequency
          useUniqueInviteCodes
          companyGroupIndex
          associatedBankAccount {
            id
            bankName
          }
          associatedLinkRequests {
            status
            id
          }
          schedule {
            displayAnchorPayDate
            displayDayOne
            displayDayTwo
          }
        }
      }
    `;

    const result = client.mutate<
      UpdateEmployerGroupMutation,
      UpdateEmployerGroupMutationVariables
    >({
      mutation: UPDATE_EMPLOYER_GROUP,
      variables: {
        id: group.id,
        name: group.name,
        userBankId: group.associatedBankAccount,
        useUniqueInviteCodes: group.useUniqueInviteCodes,
        displayAnchorPayDate: group.displayAnchorPayDate,
        displayDayOne: group.displayDayOne,
        displayDayTwo: group.displayDayTwo,
        companyId,
      },
    });
    result.then(
      (data) => {
        resolve(data.data?.updateEmployerGroup ?? null);
      },
      (error: Error) => {
        reject(error);
      }
    );
  });
}

// Delete Employer Group
function deleteEmployerGroup(
  client: ApolloClient<object>,
  { id, companyId, migratedToGroupId }: DeleteEmployerGroupMutationVariables
): Promise<DeleteEmployerGroupMutationVariables["id"]> {
  return new Promise((resolve, reject) => {
    const DELETE_GROUP = gql`
      mutation deleteEmployerGroup(
        $id: ID!
        $companyId: ID
        $migratedToGroupId: ID
      ) {
        deleteEmployerGroup(
          id: $id
          companyId: $companyId
          migratedToGroupId: $migratedToGroupId
        )
      }
    `;
    const result = client.mutate<
      DeleteEmployerGroupMutation,
      DeleteEmployerGroupMutationVariables
    >({
      mutation: DELETE_GROUP,
      variables: { id, companyId, migratedToGroupId },
    });
    result.then(
      () => {
        resolve(id);
      },
      (error: Error) => {
        reject(error);
      }
    );
  });
}

function inviteEmployees(
  client: ApolloClient<object>,
  userEmails: InviteUsersMutationVariables["userEmails"],
  groupId: string
): Promise<InviteUsersMutation["inviteUsers"] | null> {
  return new Promise((resolve, reject) => {
    const INVITE_EMPLOYEE = gql`
      mutation inviteUsers($groupId: ID!, $userEmails: [String!]) {
        inviteUsers(groupId: $groupId, userEmails: $userEmails) {
          id
          invitationCode
          companyId
          userEmail
          groupId
          expiresAt
          status
        }
      }
    `;

    const result = client.mutate<
      InviteUsersMutation,
      InviteUsersMutationVariables
    >({
      mutation: INVITE_EMPLOYEE,
      variables: {
        groupId,
        userEmails,
      },
    });
    result.then(
      (data) => {
        resolve(data.data?.inviteUsers ?? null);
      },
      (error: Error) => {
        reject(error);
      }
    );
  });
}

function resendInvitation(
  client: ApolloClient<object>,
  {
    userEmail,
    groupId,
    id: existingInviteId,
    firstName,
  }: Omit<ResendInvitationMutationVariables, "existingInviteId"> & {
    id: ResendInvitationMutationVariables["existingInviteId"];
  }
): Promise<ResendInvitationMutation["resendInvitation"] | null> {
  return new Promise((resolve, reject) => {
    const INVITE_EMPLOYEE = gql`
      mutation resendInvitation(
        $groupId: ID!
        $userEmail: String!
        $existingInviteId: ID!
        $firstName: String
      ) {
        resendInvitation(
          groupId: $groupId
          userEmail: $userEmail
          existingInviteId: $existingInviteId
          firstName: $firstName
        ) {
          id
          invitationCode
          companyId
          userEmail
          groupId
          expiresAt
          firstName
          status
        }
      }
    `;

    const result = client.mutate<
      ResendInvitationMutation,
      ResendInvitationMutationVariables
    >({
      mutation: INVITE_EMPLOYEE,
      variables: {
        groupId,
        existingInviteId,
        firstName,
        userEmail,
      },
    });
    result.then(
      (data) => {
        resolve(data.data?.resendInvitation ?? null);
      },
      (error: Error) => {
        reject(error);
      }
    );
  });
}

function revokeInvitation(
  client: ApolloClient<object>,
  invitationId: RevokeInvitationMutationVariables["invitationId"]
): Promise<RevokeInvitationMutation["revokeInvitation"] | null> {
  return new Promise((resolve, reject) => {
    const REVOKE_INVITE = gql`
      mutation revokeInvitation($invitationId: ID!) {
        revokeInvitation(invitationId: $invitationId) {
          id
          invitationCode
          companyId
          userEmail
          groupId
          expiresAt
          firstName
          status
        }
      }
    `;

    const result = client.mutate<
      RevokeInvitationMutation,
      RevokeInvitationMutationVariables
    >({
      mutation: REVOKE_INVITE,
      variables: {
        invitationId,
      },
    });
    result.then(
      (data) => {
        resolve(data.data?.revokeInvitation ?? null);
      },
      (error: Error) => {
        reject(error);
      }
    );
  });
}

function getEmployeeInvites(
  client: ApolloClient<object>
): Promise<
  GetAllEmployeeInvitesForCompanyQuery["getAllEmployeeInvitesForCompany"]
> {
  return new Promise((resolve, reject) => {
    const EMPLOYEE_INVITES = gql`
      query getAllEmployeeInvitesForCompany {
        getAllEmployeeInvitesForCompany {
          id
          invitationCode
          companyId
          userEmail
          groupId
          expiresAt
          status
        }
      }
    `;
    const result = client.query<
      GetAllEmployeeInvitesForCompanyQuery,
      GetAllEmployeeInvitesForCompanyQueryVariables
    >({
      query: EMPLOYEE_INVITES,
      fetchPolicy: "network-only",
    });
    result.then(
      (response) => {
        resolve(response.data.getAllEmployeeInvitesForCompany);
      },
      (error: Error) => {
        reject(error);
      }
    );
  });
}

function getTakeRateEmployeesForCompany(
  client: ApolloClient<object>,
  groupId: string,
  timeframe: GetTakeRateEmployeesForCompanyQueryVariables["timeframe"]
): Promise<
  GetTakeRateEmployeesForCompanyQuery["getTakeRateEmployeesForCompany"]
> {
  return new Promise((resolve, reject) => {
    const GET_TAKE_RATE_EMPLOYEES = gql`
      query getTakeRateEmployeesForCompany($groupId: ID, $timeframe: String!) {
        getTakeRateEmployeesForCompany(
          groupId: $groupId
          timeframe: $timeframe
        ) {
          notStarted
          opening
          enrolled
        }
      }
    `;
    const result = client.query<
      GetTakeRateEmployeesForCompanyQuery,
      GetTakeRateEmployeesForCompanyQueryVariables
    >({
      query: GET_TAKE_RATE_EMPLOYEES,
      variables: { groupId, timeframe },
      fetchPolicy: "network-only",
    });
    result.then(
      (response) => {
        resolve(response.data.getTakeRateEmployeesForCompany);
      },
      (error: Error) => {
        reject(error);
      }
    );
  });
}

function getLinkedEmployees(
  client: ApolloClient<object>
): Promise<GetLinkedEmployeesQuery["getLinkedEmployees"]> {
  return new Promise((resolve, reject) => {
    const LINKED_EMPLOYEES = gql`
      query getLinkedEmployees {
        getLinkedEmployees {
          id
          employeeId
          company {
            id
            name
          }
          groupWithSchedule {
            group {
              id
              name
              payrollFrequency
              companyGroupIndex
              useUniqueInviteCodes
              associatedBankAccount {
                id
              }
            }
          }
          contribution {
            amount
            id
          }
          userProfile {
            firstName
            lastName
            email
            lastFourOfSsn
            dob
          }
        }
      }
    `;
    const result = client.query<
      GetLinkedEmployeesQuery,
      GetLinkedEmployeesQueryVariables
    >({
      query: LINKED_EMPLOYEES,
    });
    result.then(
      (response) => {
        resolve(response.data.getLinkedEmployees);
      },
      (error: Error) => {
        reject(error);
      }
    );
  });
}

function getPendingLinkedEmployees(
  client: ApolloClient<object>
): Promise<GetEmployerLinkRequestsQuery["getEmployerLinkRequests"]> {
  return new Promise((resolve, reject) => {
    const LINKED_EMPLOYEES = gql`
      query getEmployerLinkRequests {
        getEmployerLinkRequests {
          id
          status
          employeeId
          company {
            id
            name
          }
          group {
            useUniqueInviteCodes
            id
            name
          }
          userProfile {
            firstName
            lastName
            email
            dob
            lastFourOfSsn
          }
        }
      }
    `;
    const result = client.query<
      GetEmployerLinkRequestsQuery,
      GetEmployerLinkRequestsQueryVariables
    >({
      query: LINKED_EMPLOYEES,
    });
    result.then(
      (response) => {
        resolve(response.data.getEmployerLinkRequests);
      },
      (error: Error) => {
        reject(error);
      }
    );
  });
}

function getUserProfilesForGroup(
  client: ApolloClient<object>,
  groupId: string
): Promise<GetUserProfilesForGroupQuery["getUserProfilesForGroup"]> {
  return new Promise((resolve, reject) => {
    const GET_USER_PROFILES = gql`
      query getUserProfilesForGroup($groupId: ID!) {
        getUserProfilesForGroup(groupId: $groupId) {
          hasMetadata
          employees {
            id
            firstName
            lastName
            lastFourOfSsn
            state
            directoryInfo {
              location
              department
            }
          }
        }
      }
    `;
    const result = client.query<
      GetUserProfilesForGroupQuery,
      GetUserProfilesForGroupQueryVariables
    >({
      query: GET_USER_PROFILES,
      variables: { groupId },
    });
    result.then(
      (response) => {
        resolve(response.data.getUserProfilesForGroup);
      },
      (error: Error) => {
        reject(error);
      }
    );
  });
}

function approvePendingLinkedEmployees(
  client: ApolloClient<object>,
  linkIds: ApproveEmployerLinkRequestsMutationVariables["requestIds"]
): Promise<
  ApproveEmployerLinkRequestsMutation["approveEmployerLinkRequests"] | null
> {
  return new Promise((resolve, reject) => {
    const APPROVE_LINK = gql`
      mutation approveEmployerLinkRequests($requestIds: [ID!]) {
        approveEmployerLinkRequests(requestIds: $requestIds) {
          id
          status
          employeeId
          company {
            id
            name
          }
          group {
            id
            name
          }
          userProfile {
            firstName
            lastName
            email
            dob
            lastFourOfSsn
          }
        }
      }
    `;

    const result = client.mutate<
      ApproveEmployerLinkRequestsMutation,
      ApproveEmployerLinkRequestsMutationVariables
    >({
      mutation: APPROVE_LINK,
      variables: {
        requestIds: linkIds,
      },
    });
    result.then(
      (data) => {
        resolve(data.data?.approveEmployerLinkRequests ?? null);
      },
      (error: Error) => {
        reject(error);
      }
    );
  });
}

function rejectPendingLinkedEmployees(
  client: ApolloClient<object>,
  linkIds: RejectEmployerLinkRequestsMutationVariables["requestIds"]
): Promise<
  RejectEmployerLinkRequestsMutation["rejectEmployerLinkRequests"] | null
> {
  return new Promise((resolve, reject) => {
    const REJECT_LINK = gql`
      mutation rejectEmployerLinkRequests($requestIds: [ID!]) {
        rejectEmployerLinkRequests(requestIds: $requestIds) {
          id
          status
          employeeId
          company {
            id
            name
          }
          group {
            id
            name
          }
          userProfile {
            firstName
            lastName
            email
            dob
            lastFourOfSsn
          }
        }
      }
    `;

    const result = client.mutate<
      RejectEmployerLinkRequestsMutation,
      RejectEmployerLinkRequestsMutationVariables
    >({
      mutation: REJECT_LINK,
      variables: {
        requestIds: linkIds,
      },
    });
    result.then(
      (data) => {
        console.log(data);
        resolve(data.data?.rejectEmployerLinkRequests ?? null);
      },
      (error: Error) => {
        console.log(error);
        reject(error);
      }
    );
  });
}

function processPayroll(
  client: ApolloClient<object>,
  payrollInput: ProcessPayrollMutationVariables["payrollInput"]
): Promise<ProcessPayrollMutation["processPayroll"] | null> {
  return new Promise((resolve, reject) => {
    const PROCESS_PAYROLL = gql`
      mutation processPayroll($payrollInput: PayrollInput!) {
        processPayroll(input: $payrollInput)
      }
    `;

    const result = client.mutate<
      ProcessPayrollMutation,
      ProcessPayrollMutationVariables
    >({
      mutation: PROCESS_PAYROLL,
      variables: {
        payrollInput,
      },
    });
    result.then(
      (data) => {
        resolve(data.data?.processPayroll ?? null);
      },
      (error: Error) => {
        reject(error);
      }
    );
  });
}

function approveContributionChanges(
  client: ApolloClient<object>,
  contributionChangeIds: ApproveContributionChangesMutationVariables["contributionChangeIds"],
  groupId: string
): Promise<
  ApproveContributionChangesMutation["approveContributionChanges"] | null
> {
  return new Promise((resolve, reject) => {
    const APPROVE_CONTRIBUTIONS = gql`
      mutation approveContributionChanges(
        $contributionChangeIds: [ID!]
        $groupId: ID!
      ) {
        approveContributionChanges(
          contributionChangeIds: $contributionChangeIds
          groupId: $groupId
        ) {
          id
          status
        }
      }
    `;

    const result = client.mutate<
      ApproveContributionChangesMutation,
      ApproveContributionChangesMutationVariables
    >({
      mutation: APPROVE_CONTRIBUTIONS,
      variables: {
        contributionChangeIds,
        groupId,
      },
    });
    result.then(
      (data) => {
        console.log(data);
        resolve(data.data?.approveContributionChanges ?? null);
      },
      (error: Error) => {
        console.log(error);
        reject(error);
      }
    );
  });
}

function rejectContributionChanges(
  client: ApolloClient<object>,
  contributionRejections: RejectContributionChangesMutationVariables["contributionRejections"],
  groupId: string
): Promise<
  RejectContributionChangesMutation["rejectContributionChanges"] | null
> {
  return new Promise((resolve, reject) => {
    const REJECT_CONTRIBUTIONS = gql`
      mutation rejectContributionChanges(
        $contributionRejections: [ContributionRejection!]
        $groupId: ID!
      ) {
        rejectContributionChanges(
          contributionRejections: $contributionRejections
          groupId: $groupId
        ) {
          id
          status
        }
      }
    `;

    const result = client.mutate<
      RejectContributionChangesMutation,
      RejectContributionChangesMutationVariables
    >({
      mutation: REJECT_CONTRIBUTIONS,
      variables: {
        contributionRejections,
        groupId,
      },
    });
    result.then(
      (data) => {
        console.log(data);
        resolve(data.data?.rejectContributionChanges ?? null);
      },
      (error: Error) => {
        console.log(error);
        reject(error);
      }
    );
  });
}

function deleteEmployerLinks(
  client: ApolloClient<object>,
  linkIds: DeleteEmployerLinksMutationVariables["linkIds"]
): Promise<DeleteEmployerLinksMutation["deleteEmployerLinks"] | null> {
  return new Promise((resolve, reject) => {
    const DELETE_EMPLOYER_LINKS = gql`
      mutation deleteEmployerLinks($linkIds: [ID!]) {
        deleteEmployerLinks(linkIds: $linkIds)
      }
    `;

    const result = client.mutate<
      DeleteEmployerLinksMutation,
      DeleteEmployerLinksMutationVariables
    >({
      mutation: DELETE_EMPLOYER_LINKS,
      variables: {
        linkIds,
      },
    });
    result.then(
      (data) => {
        console.log(data);
        resolve(data.data?.deleteEmployerLinks ?? null);
      },
      (error: Error) => {
        console.log(error);
        reject(error);
      }
    );
  });
}

function getAdministrators(
  client: ApolloClient<object>
): Promise<GetAllAgentLinksForCompanyQuery["getAllAgentLinksForCompany"]> {
  return new Promise((resolve, reject) => {
    const GET_AGENTS = gql`
      query getAllAgentLinksForCompany {
        getAllAgentLinksForCompany {
          user {
            id
            email
            username
            userRoleId
          }
          link {
            id
            invitationExpiresAt
            status
            companyId
            locked
            role
          }
        }
      }
    `;
    const result = client.query<
      GetAllAgentLinksForCompanyQuery,
      GetAllAgentLinksForCompanyQueryVariables
    >({
      query: GET_AGENTS,
      variables: {},
      fetchPolicy: "network-only",
    });
    result.then(
      (response) => {
        if (response.data) {
          resolve(response.data.getAllAgentLinksForCompany);
        } else {
          // eslint-disable-next-line @typescript-eslint/prefer-promise-reject-errors -- pre-existing contract: rejects the raw Apollo response
          reject(response);
        }
      },
      (error: Error) => {
        reject(error);
      }
    );
  });
}

function resendAdministratorInvite(
  client: ApolloClient<object>,
  { agentId, companyId }: ResendAgentInvitationMutationVariables
): Promise<ResendAgentInvitationMutation["resendAgentInvitation"]> {
  return new Promise((resolve, reject) => {
    const RESEND_INVITATION = gql`
      mutation resendAgentInvitation($agentId: ID!, $companyId: ID) {
        resendAgentInvitation(agentId: $agentId, companyId: $companyId) {
          link {
            id
            invitationExpiresAt
            status
            locked
          }
        }
      }
    `;
    const result = client.mutate<
      ResendAgentInvitationMutation,
      ResendAgentInvitationMutationVariables
    >({
      mutation: RESEND_INVITATION,
      variables: { agentId, companyId },
    });
    result.then(
      (response) => {
        if (response.data) {
          resolve(response.data.resendAgentInvitation);
        } else {
          // eslint-disable-next-line @typescript-eslint/prefer-promise-reject-errors -- pre-existing contract: rejects the raw Apollo response
          reject(response);
        }
      },
      (error: Error) => {
        reject(error);
      }
    );
  });
}

function inviteAdministrator(
  client: ApolloClient<object>,
  {
    email,
    username,
    firstName,
    companyId,
    role,
    lastName,
  }: InviteEmployerAgentMutationVariables
): Promise<InviteEmployerAgentMutation["inviteEmployerAgent"]> {
  return new Promise((resolve, reject) => {
    const SEND_INVITATION = gql`
      mutation inviteEmployerAgent(
        $email: String!
        $username: String!
        $firstName: String!
        $lastName: String!
        $role: String
        $companyId: ID
      ) {
        inviteEmployerAgent(
          email: $email
          username: $username
          firstName: $firstName
          lastName: $lastName
          role: $role
          companyId: $companyId
        ) {
          user {
            id
            email
            username
          }
          link {
            id
            invitationExpiresAt
            status
            companyId
            locked
            role
          }
        }
      }
    `;
    const result = client.mutate<
      InviteEmployerAgentMutation,
      InviteEmployerAgentMutationVariables
    >({
      mutation: SEND_INVITATION,
      variables: { email, username, firstName, role, companyId, lastName },
    });
    result.then(
      (response) => {
        if (response.data) {
          resolve(response.data.inviteEmployerAgent);
        } else {
          // eslint-disable-next-line @typescript-eslint/prefer-promise-reject-errors -- pre-existing contract: rejects the raw Apollo response
          reject(response);
        }
      },
      (error: Error) => {
        reject(error);
      }
    );
  });
}

function lockAdministrator(
  client: ApolloClient<object>,
  { agentId, companyId }: LockAgentMutationVariables
): Promise<LockAgentMutation["lockAgent"]> {
  return new Promise((resolve, reject) => {
    const LOCK_ADMINISTRATOR = gql`
      mutation lockAgent($agentId: ID!, $companyId: ID) {
        lockAgent(agentId: $agentId, companyId: $companyId) {
          user {
            id
          }
          link {
            id
            status
            locked
          }
        }
      }
    `;
    const result = client.mutate<LockAgentMutation, LockAgentMutationVariables>(
      {
        mutation: LOCK_ADMINISTRATOR,
        variables: { agentId, companyId },
      }
    );
    result.then(
      (response) => {
        if (response.data) {
          resolve(response.data.lockAgent);
        } else {
          // eslint-disable-next-line @typescript-eslint/prefer-promise-reject-errors -- pre-existing contract: rejects the raw Apollo response
          reject(response);
        }
      },
      (error: Error) => {
        reject(error);
      }
    );
  });
}

function unlockAdministrator(
  client: ApolloClient<object>,
  { agentId, companyId }: UnlockAgentMutationVariables
): Promise<UnlockAgentMutation["unlockAgent"]> {
  return new Promise((resolve, reject) => {
    const UNLOCK_ADMINISTRATOR = gql`
      mutation unlockAgent($agentId: ID!, $companyId: ID) {
        unlockAgent(agentId: $agentId, companyId: $companyId) {
          user {
            id
          }
          link {
            id
            status
            locked
          }
        }
      }
    `;
    const result = client.mutate<
      UnlockAgentMutation,
      UnlockAgentMutationVariables
    >({
      mutation: UNLOCK_ADMINISTRATOR,
      variables: { agentId, companyId },
    });
    result.then(
      (response) => {
        if (response.data) {
          resolve(response.data.unlockAgent);
        } else {
          // eslint-disable-next-line @typescript-eslint/prefer-promise-reject-errors -- pre-existing contract: rejects the raw Apollo response
          reject(response);
        }
      },
      (error: Error) => {
        reject(error);
      }
    );
  });
}

function uploadEmployeesDocumentRequest(
  client: ApolloClient<object>,
  groupId: string,
  file: UploadEmployeeDocumentMutationVariables["file"]
): Promise<UploadEmployeeDocumentMutation["uploadEmployeeDocument"] | null> {
  return new Promise((resolve, reject) => {
    const REQUEST = gql`
      mutation uploadEmployeeDocument($groupId: ID!, $file: DocumentInput!) {
        uploadEmployeeDocument(groupId: $groupId, file: $file) {
          id
          documentId
          message
          status
          groupId
          filename
          createdAt
          group {
            planType
            name
          }
        }
      }
    `;
    const result = client.mutate<
      UploadEmployeeDocumentMutation,
      UploadEmployeeDocumentMutationVariables
    >({
      mutation: REQUEST,
      variables: {
        groupId,
        file,
      },
    });
    result.then(
      (data) => {
        resolve(data.data?.uploadEmployeeDocument ?? null);
      },
      (error: Error) => {
        reject(error);
      }
    );
  });
}

function getEmployeeDocumentUploadJobsRequest(
  client: ApolloClient<object>,
  companyId: string
): Promise<
  GetEmployeeDocumentUploadJobsQuery["getEmployeeDocumentUploadJobs"]
> {
  return new Promise((resolve, reject) => {
    const REQUEST = gql`
      query getEmployeeDocumentUploadJobs($companyId: ID) {
        getEmployeeDocumentUploadJobs(companyId: $companyId) {
          id
          documentId
          message
          status
          groupId
          filename
          createdAt
          group {
            planType
            name
          }
          errors {
            reason
            type
            format
          }
        }
      }
    `;
    const result = client.query<
      GetEmployeeDocumentUploadJobsQuery,
      GetEmployeeDocumentUploadJobsQueryVariables
    >({
      query: REQUEST,
      variables: { companyId },
    });
    result.then(
      (data) => {
        resolve(data.data.getEmployeeDocumentUploadJobs);
      },
      (error: Error) => {
        reject(error);
      }
    );
  });
}

function getPaginatedEmployees(
  client: ApolloClient<object>,
  { limit, offset, search, groupId }: GetPaginatedEmployeesQueryVariables
): Promise<GetPaginatedEmployeesQuery["getPaginatedEmployees"]> {
  return new Promise((resolve, reject) => {
    const REQUEST = gql`
      query getPaginatedEmployees(
        $limit: Int!
        $offset: Int!
        $groupId: ID!
        $search: String
      ) {
        getPaginatedEmployees(
          limit: $limit
          offset: $offset
          search: $search
          groupId: $groupId
        ) {
          count
          rows {
            id
            employeeId
            company {
              id
              name
            }
            groupWithSchedule {
              group {
                id
                name
                payrollFrequency
                companyGroupIndex
                useUniqueInviteCodes
                associatedBankAccount {
                  id
                }
              }
            }
            contribution {
              amount
              id
              contributionChangeRequests {
                status
                id
                userCurrentContributionAmount
                userNewContributionAmount
              }
            }
            userProfile {
              firstName
              lastName
              email
              lastFourOfSsn
              dob
            }
          }
        }
      }
    `;
    const result = client.query<
      GetPaginatedEmployeesQuery,
      GetPaginatedEmployeesQueryVariables
    >({
      query: REQUEST,
      variables: { limit, offset, search, groupId },
    });
    result.then(
      (data) => {
        resolve(data.data.getPaginatedEmployees);
      },
      (error: Error) => {
        reject(error);
      }
    );
  });
}

function resendInvitations(
  client: ApolloClient<object>,
  {
    invitations,
    groupIds,
  }: Omit<ResendInvitationsMutationVariables, "invitationIds"> & {
    invitations: ResendInvitationsMutationVariables["invitationIds"];
  }
): Promise<ResendInvitationsMutation["resendInvitations"] | null> {
  return new Promise((resolve, reject) => {
    const request = gql`
      mutation resendInvitations($invitationIds: [ID!], $groupIds: [ID!]) {
        resendInvitations(invitationIds: $invitationIds, groupIds: $groupIds) {
          id
          invitationCode
          companyId
          userEmail
          groupId
          expiresAt
          firstName
          status
          createdAt
        }
      }
    `;

    const result = client.mutate<
      ResendInvitationsMutation,
      ResendInvitationsMutationVariables
    >({
      mutation: request,
      variables: {
        invitationIds: invitations,
        groupIds,
      },
    });
    result.then(
      (data) => {
        resolve(data.data?.resendInvitations ?? null);
      },
      (error: Error) => {
        reject(error);
      }
    );
  });
}

function setEmployerSession(
  client: ApolloClient<object>,
  companyId: string
): Promise<SetEmployerSessionMutation["setEmployerSession"] | null> {
  return new Promise((resolve, reject) => {
    const request = gql`
      mutation setEmployerSession($companyId: ID!) {
        setEmployerSession(companyId: $companyId)
      }
    `;

    const result = client.mutate<
      SetEmployerSessionMutation,
      SetEmployerSessionMutationVariables
    >({
      mutation: request,
      variables: {
        companyId,
      },
    });
    result.then(
      (data) => {
        resolve(data.data?.setEmployerSession ?? null);
      },
      (error: Error) => {
        reject(error);
      }
    );
  });
}

function getLinkedCompanies(
  client: ApolloClient<object>
): Promise<GetLinkedCompaniesQuery["getLinkedCompanies"]> {
  return new Promise((resolve, reject) => {
    const REQUEST = gql`
      query getLinkedCompanies {
        getLinkedCompanies {
          companies {
            id
            name
          }
          organization {
            id
            name
            companyLimit
          }
        }
      }
    `;
    const result = client.query<
      GetLinkedCompaniesQuery,
      GetLinkedCompaniesQueryVariables
    >({
      query: REQUEST,
      variables: {},
    });
    result.then(
      (data) => {
        resolve(data.data.getLinkedCompanies);
      },
      (error: Error) => {
        reject(error);
      }
    );
  });
}

export function getPaginatedInvites(
  client: ApolloClient<object>,
  { limit, offset, search, groupId }: GetPaginatedInvitesQueryVariables
): Promise<GetPaginatedInvitesQuery["getPaginatedInvites"]> {
  return new Promise((resolve, reject) => {
    const REQUEST = gql`
      query getPaginatedInvites(
        $limit: Int!
        $offset: Int!
        $search: String
        $groupId: ID
      ) {
        getPaginatedInvites(
          limit: $limit
          offset: $offset
          search: $search
          groupId: $groupId
        ) {
          count
          rows {
            id
            invitationCode
            companyId
            userEmail
            groupId
            expiresAt
            status
            firstName
            createdAt
          }
        }
      }
    `;
    const result = client.query<
      GetPaginatedInvitesQuery,
      GetPaginatedInvitesQueryVariables
    >({
      query: REQUEST,
      variables: {
        limit,
        offset,
        search,
        groupId,
      },
    });
    result.then(
      (data) => {
        resolve(data.data.getPaginatedInvites);
      },
      (error: Error) => {
        reject(error);
      }
    );
  });
}

function changeAdministratorRole(
  client: ApolloClient<object>,
  { agentId, role, companyId }: ChangeRoleMutationVariables
): Promise<ChangeRoleMutation["changeRole"] | null> {
  return new Promise((resolve, reject) => {
    const request = gql`
      mutation changeRole($agentId: ID!, $role: String!, $companyId: ID) {
        changeRole(agentId: $agentId, role: $role, companyId: $companyId) {
          link {
            id
            invitationExpiresAt
            status
            locked
            role
          }
        }
      }
    `;

    const result = client.mutate<
      ChangeRoleMutation,
      ChangeRoleMutationVariables
    >({
      mutation: request,
      variables: {
        agentId,
        companyId,
        role,
      },
    });
    result.then(
      (data) => {
        resolve(data.data?.changeRole ?? null);
      },
      (error: Error) => {
        reject(error);
      }
    );
  });
}

function getBeneficialOwners(
  client: ApolloClient<object>
): Promise<GetBeneficialOwnersQuery["getBeneficialOwners"]> {
  return new Promise((resolve, reject) => {
    const REQUEST = gql`
      query getBeneficialOwners {
        getBeneficialOwners {
          id
          businessMemberHandle
          role
          ownershipStake
          state
          memberProfile {
            legalName
            firstName
            lastName
            alias
            email
            phoneNumber
            ssn
            citizenshipCountry
            birthCountry
            permanentResident
            dob
            address1
            address2
            city
            state
            postalCode
            maritalStatus
            numberOfDependents
            employeeId
            phone
          }
        }
      }
    `;
    const result = client.query<
      GetBeneficialOwnersQuery,
      GetBeneficialOwnersQueryVariables
    >({
      query: REQUEST,
      variables: {},
    });
    result.then(
      (data) => {
        resolve(data.data.getBeneficialOwners);
      },
      (error: Error) => {
        reject(error);
      }
    );
  });
}

function validateSelfServe(
  client: ApolloClient<object>
): Promise<ValidateSelfServeMutation["validateSelfServe"] | null> {
  return new Promise((resolve, reject) => {
    const request = gql`
      mutation validateSelfServe {
        validateSelfServe
      }
    `;

    const result = client.mutate<
      ValidateSelfServeMutation,
      ValidateSelfServeMutationVariables
    >({
      mutation: request,
      variables: {},
    });
    result.then(
      (data) => {
        resolve(data.data?.validateSelfServe ?? null);
      },
      (error: Error) => {
        reject(error);
      }
    );
  });
}

function getSetupIntent(
  client: ApolloClient<object>
): Promise<GetSetupIntentQuery["getSetupIntent"]> {
  return new Promise((resolve, reject) => {
    const REQUEST = gql`
      query getSetupIntent {
        getSetupIntent {
          client_secret
          status
          payment_method
          next_action {
            type
            verify_with_microdeposits {
              arrival_date
              hosted_verification_url
              microdeposit_type
            }
          }
          last_setup_error {
            code
            decline_code
            message
          }
        }
      }
    `;
    const result = client.query<
      GetSetupIntentQuery,
      GetSetupIntentQueryVariables
    >({
      query: REQUEST,
      variables: {},
    });
    result.then(
      (data) => {
        resolve(data.data?.getSetupIntent ?? null);
      },
      (error: Error) => {
        reject(error);
      }
    );
  });
}

function getPaymentRequirement(
  client: ApolloClient<object>
): Promise<GetPaymentRequirementQuery["getPaymentRequirement"]> {
  return new Promise((resolve, reject) => {
    const REQUEST = gql`
      query getPaymentRequirement {
        getPaymentRequirement {
          collectsPaymentMethod
          requiresPaymentMethod
          openInvoiceAmountDue
          billingStartDate
        }
      }
    `;
    const result = client.query<
      GetPaymentRequirementQuery,
      GetPaymentRequirementQueryVariables
    >({
      query: REQUEST,
      variables: {},
    });
    result.then(
      (data) => {
        resolve(data.data?.getPaymentRequirement ?? null);
      },
      (error: Error) => {
        reject(error);
      }
    );
  });
}

function createSetupIntent(
  client: ApolloClient<object>
): Promise<CreateSetupIntentMutation["createSetupIntent"] | null> {
  return new Promise((resolve, reject) => {
    const REQUEST = gql`
      mutation createSetupIntent {
        createSetupIntent {
          client_secret
          status
          payment_method
        }
      }
    `;

    const result = client.mutate<
      CreateSetupIntentMutation,
      CreateSetupIntentMutationVariables
    >({
      mutation: REQUEST,
      variables: {},
    });

    result.then(
      (data) => {
        resolve(data.data?.createSetupIntent ?? null);
      },
      (error: Error) => {
        reject(error);
      }
    );
  });
}

function setInvoicePaymentMethod(
  client: ApolloClient<object>,
  paymentMethod: SetInvoicePaymentMethodMutationVariables["paymentMethod"]
): Promise<SetInvoicePaymentMethodMutation["setInvoicePaymentMethod"] | null> {
  return new Promise((resolve, reject) => {
    const REQUEST = gql`
      mutation setInvoicePaymentMethod($paymentMethod: String!) {
        setInvoicePaymentMethod(paymentMethod: $paymentMethod)
      }
    `;

    const result = client.mutate<
      SetInvoicePaymentMethodMutation,
      SetInvoicePaymentMethodMutationVariables
    >({
      mutation: REQUEST,
      variables: { paymentMethod },
    });
    result.then(
      (data) => {
        resolve(data.data?.setInvoicePaymentMethod ?? null);
      },
      (error: Error) => {
        reject(error);
      }
    );
  });
}

function getBillingPortalURL(
  client: ApolloClient<object>,
  returnUrl: string
): Promise<GetBillingPortalUrlQuery["getBillingPortalURL"]> {
  return new Promise((resolve, reject) => {
    const REQUEST = gql`
      query getBillingPortalURL($returnUrl: String!) {
        getBillingPortalURL(returnUrl: $returnUrl)
      }
    `;
    const result = client.query<
      GetBillingPortalUrlQuery,
      GetBillingPortalUrlQueryVariables
    >({
      query: REQUEST,
      variables: { returnUrl },
    });
    result.then(
      (data) => {
        resolve(data.data?.getBillingPortalURL ?? null);
      },
      (error: Error) => {
        reject(error);
      }
    );
  });
}

function getProgressiveOnboarding(
  client: ApolloClient<object>
): Promise<GetProgressiveOnboardingQuery["getProgressiveOnboarding"]> {
  return new Promise((resolve, reject) => {
    const GET_PROGRESSIVE_ONBOARDING = gql`
      query getProgressiveOnboarding {
        getProgressiveOnboarding
      }
    `;
    const result = client.query<
      GetProgressiveOnboardingQuery,
      GetProgressiveOnboardingQueryVariables
    >({
      query: GET_PROGRESSIVE_ONBOARDING,
      variables: {},
    });
    result.then(
      (response) => {
        resolve(response.data.getProgressiveOnboarding);
      },
      (error: Error) => {
        reject(error);
      }
    );
  });
}

function updateProgressiveOnboarding(
  client: ApolloClient<object>,
  eventName: UpdateProgressiveOnboardingMutationVariables["eventName"],
  companyId: string
): Promise<
  UpdateProgressiveOnboardingMutation["updateProgressiveOnboarding"] | null
> {
  return new Promise((resolve, reject) => {
    const UPDATE_PROGRESSIVE_ONBOARDING = gql`
      mutation updateProgressiveOnboarding(
        $eventName: String!
        $companyId: String
      ) {
        updateProgressiveOnboarding(
          eventName: $eventName
          companyId: $companyId
        )
      }
    `;
    const result = client.mutate<
      UpdateProgressiveOnboardingMutation,
      UpdateProgressiveOnboardingMutationVariables
    >({
      mutation: UPDATE_PROGRESSIVE_ONBOARDING,
      variables: {
        eventName,
        companyId,
      },
    });
    result.then(
      (data) => {
        resolve(data.data?.updateProgressiveOnboarding ?? null);
      },
      (error: Error) => {
        reject(error);
      }
    );
  });
}

function uploadRoster(
  client: ApolloClient<object>,
  { rows, groupId, companyId }: UploadRosterMutationVariables
): Promise<UploadRosterMutation["uploadRoster"] | null> {
  return new Promise((resolve, reject) => {
    const REQUEST = gql`
      mutation uploadRoster(
        $groupId: ID!
        $rows: [RosterRow!]!
        $companyId: ID
      ) {
        uploadRoster(groupId: $groupId, rows: $rows, companyId: $companyId) {
          id
          documentId
          message
          status
          groupId
          filename
          createdAt
          group {
            planType
            name
          }
          errors {
            reason
            type
            format
          }
        }
      }
    `;

    const result = client.mutate<
      UploadRosterMutation,
      UploadRosterMutationVariables
    >({
      mutation: REQUEST,
      variables: { rows, groupId, companyId },
    });
    result.then(
      (data) => {
        resolve(data.data?.uploadRoster ?? null);
      },
      (error: Error) => {
        reject(error);
      }
    );
  });
}

function stageEmployeeInvites(
  client: ApolloClient<object>,
  { groupId, userEmails }: StageUserInvitesMutationVariables
): Promise<StageUserInvitesMutation["stageUserInvites"] | null> {
  return new Promise((resolve, reject) => {
    const STAGE_EMPLOYEE_INVITE = gql`
      mutation stageUserInvites($groupId: ID!, $userEmails: [String!]) {
        stageUserInvites(groupId: $groupId, userEmails: $userEmails)
      }
    `;

    const result = client.mutate<
      StageUserInvitesMutation,
      StageUserInvitesMutationVariables
    >({
      mutation: STAGE_EMPLOYEE_INVITE,
      variables: {
        groupId,
        userEmails,
      },
    });
    result.then(
      (data) => {
        resolve(data.data?.stageUserInvites ?? null);
      },
      (error: Error) => {
        reject(error);
      }
    );
  });
}

function resyncEmployeeRoster(
  client: ApolloClient<object>
): Promise<ResyncEmployeeRosterMutation["resyncEmployeeRoster"] | null> {
  return new Promise((resolve, reject) => {
    const MUTATION = gql`
      mutation resyncEmployeeRoster {
        resyncEmployeeRoster
      }
    `;

    const result = client.mutate<
      ResyncEmployeeRosterMutation,
      ResyncEmployeeRosterMutationVariables
    >({
      mutation: MUTATION,
      variables: {},
    });
    result.then(
      (data) => {
        resolve(data.data?.resyncEmployeeRoster ?? null);
      },
      (error: Error) => {
        reject(error);
      }
    );
  });
}

function processRoster(
  client: ApolloClient<object>,
  companyId?: string
): Promise<ProcessRosterMutation["processRoster"] | null> {
  return new Promise((resolve, reject) => {
    const MUTATION = gql`
      mutation processRoster($companyId: ID) {
        processRoster(companyId: $companyId)
      }
    `;

    const result = client.mutate<
      ProcessRosterMutation,
      ProcessRosterMutationVariables
    >({
      mutation: MUTATION,
      variables: { companyId },
    });
    result.then(
      (data) => {
        resolve(data.data?.processRoster ?? null);
      },
      (error: Error) => {
        reject(error);
      }
    );
  });
}

function approveRoster(
  client: ApolloClient<object>,
  rejectedEmployeeIds: ApproveRosterMutationVariables["rejectedEmployeeIds"]
): Promise<ApproveRosterMutation["approveRoster"] | null> {
  return new Promise((resolve, reject) => {
    const MUTATION = gql`
      mutation approveRoster($rejectedEmployeeIds: [ID!]) {
        approveRoster(rejectedEmployeeIds: $rejectedEmployeeIds)
      }
    `;

    const result = client.mutate<
      ApproveRosterMutation,
      ApproveRosterMutationVariables
    >({
      mutation: MUTATION,
      variables: { rejectedEmployeeIds },
    });
    result.then(
      (data) => {
        resolve(data.data?.approveRoster ?? null);
      },
      (error: Error) => {
        reject(error);
      }
    );
  });
}

function getEmployeeRoster(
  client: ApolloClient<object>,
  { offset, limit, filter }: GetEmployeeRosterQueryVariables
): Promise<GetEmployeeRosterQuery["getEmployeeRoster"]> {
  return new Promise((resolve, reject) => {
    const QUERY = gql`
      query getEmployeeRoster($offset: Int!, $limit: Int!, $filter: String) {
        getEmployeeRoster(offset: $offset, limit: $limit, filter: $filter) {
          totalRows
          currentOffset
          limit
          filterValues {
            state
          }
          results {
            email
            phoneNumber
            employeeId
            dob
            firstName
            lastName
            address {
              line1
              line2
              city
              state
              postalCode
              country
            }
            socialSecurityNumber
            status
            linkStatus
          }
        }
      }
    `;

    const result = client.query<
      GetEmployeeRosterQuery,
      GetEmployeeRosterQueryVariables
    >({
      query: QUERY,
      variables: {
        offset,
        limit,
        filter,
      },
    });
    result.then(
      (data) => {
        resolve(data.data.getEmployeeRoster);
      },
      (error: Error) => {
        reject(error);
      }
    );
  });
}

function referCompany(
  client: ApolloClient<object>,
  email: string
): Promise<ReferCompanyMutation["referCompany"] | null> {
  return new Promise((resolve, reject) => {
    const MUTATION = gql`
      mutation referCompany($email: String!) {
        referCompany(email: $email)
      }
    `;

    const result = client.mutate<
      ReferCompanyMutation,
      ReferCompanyMutationVariables
    >({
      mutation: MUTATION,
      variables: {
        email,
      },
    });
    result.then(
      (data) => {
        resolve(data.data?.referCompany ?? null);
      },
      (error: Error) => {
        reject(error);
      }
    );
  });
}

export function getPayrollGroupContributionAbilityRequest(
  client: ApolloClient<object>,
  groupId: string
): Promise<
  | NonNullable<
      NonNullable<
        GetPayrollGroupContributionAbilityQuery["getEmployerGroup"]
      >["contributionAbility"]
    >
  | object
> {
  return new Promise((resolve, reject) => {
    const request = gql`
      query getPayrollGroupContributionAbility($groupId: ID!) {
        getEmployerGroup(groupId: $groupId) {
          id
          contributionAbility {
            payrollPriorYearCutoffDate
            priorYearCutoffDate
            isWithinPayrollPriorYearContributionWindow
            isWithinPriorYearContributionWindow
            currentYear {
              taxYear
              amountRemaining
              canProcessPayroll
            }
            priorYear {
              taxYear
              amountRemaining
              canProcessPayroll
            }
            allYears {
              taxYear
              amountRemaining
              canProcessPayroll
            }

            userContributionAbilities {
              userId
              currentYear {
                taxYear
                totalContributionAmount
                amountRemaining
                limit
                canContribute
              }
              priorYear {
                taxYear
                totalContributionAmount
                amountRemaining
                limit
                canContribute
              }
              isWithinPriorYearContributionWindow
              allYears {
                taxYear
                totalContributionAmount
                amountRemaining
                limit
                canContribute
              }
            }
          }
        }
      }
    `;
    const result = client.query<
      GetPayrollGroupContributionAbilityQuery,
      GetPayrollGroupContributionAbilityQueryVariables
    >({
      query: request,
      variables: {
        groupId,
      },
    });
    result.then(
      (data) => {
        resolve(get(data, "data.getEmployerGroup.contributionAbility", {}));
      },
      (error: Error) => {
        reject(error);
      }
    );
  });
}

function currentPayrolls(
  client: ApolloClient<object>,
  companyId: string
): Promise<CurrentPayrollsQuery["currentPayrolls"]> {
  return new Promise((resolve, reject) => {
    const CURRENT_PAYROLLS = gql`
      query currentPayrolls($companyId: ID) {
        currentPayrolls(companyId: $companyId) {
          payrollId
          start
          end
          payrollProcessingDate
          onOrAfterPayrollProcessingDate
          lastPayrollApprovalDate
          status
          gracePeriodEndDate
          payPeriodStart
          payPeriodEnd
          periodState
          payPeriodId
          groupId
          userCount
          payrollType
          amount
          taxYear
          adhocEligible
        }
      }
    `;
    const result = client.query<
      CurrentPayrollsQuery,
      CurrentPayrollsQueryVariables
    >({
      query: CURRENT_PAYROLLS,
      variables: {
        companyId,
      },
      fetchPolicy: "network-only",
    });
    result.then(
      (data) => {
        resolve(data.data.currentPayrolls);
      },
      (error: Error) => {
        reject(error);
      }
    );
  });
}

function hydrateDashboard(
  client: ApolloClient<object>
): Promise<HydrateDashboardQuery> {
  return new Promise((resolve, reject) => {
    const DASHBOARD_HYDRATE = gql`
      query hydrateDashboard {
        employerAccountList {
          accountId
          id
          accountType
          accountSubtype
          userId
          bankName
          bankAlias
          linkStatus
          state
          loginRequired
        }
        getEmployerGroups {
          id
          name
          payrollFrequency
          useUniqueInviteCodes
          associatedBankAccount {
            id
            bankName
          }
          associatedLinkRequests {
            status
            id
          }
          schedule {
            anchorPayDate
            displayAnchorPayDate
            displayDayOne
            displayDayTwo
          }
          planType
          companyGroupIndex
        }
        getAllEmployeeInvitesForCompany {
          id
          invitationCode
          companyId
          userEmail
          groupId
          expiresAt
          status
        }
        getEmployerLinkRequests {
          id
          status
          employeeId
          company {
            id
            name
          }
          group {
            useUniqueInviteCodes
            id
            name
          }
          userProfile {
            firstName
            lastName
            email
            dob
            lastFourOfSsn
          }
        }
        getTotalAmountSaved {
          totalAmount
        }
        getLinkedEmployees {
          id
          employeeId
          company {
            id
            name
          }
          groupWithSchedule {
            group {
              id
              name
              payrollFrequency
              companyGroupIndex
              useUniqueInviteCodes
              associatedBankAccount {
                id
              }
            }
          }
          contribution {
            amount
            id
          }
          userProfile {
            firstName
            lastName
            email
            lastFourOfSsn
            dob
          }
        }
      }
    `;

    const result = client.query<
      HydrateDashboardQuery,
      HydrateDashboardQueryVariables
    >({
      query: DASHBOARD_HYDRATE,
      variables: {},
      fetchPolicy: "network-only",
    });
    result.then(
      (data) => {
        resolve(data.data);
      },
      (error: Error) => {
        reject(error);
      }
    );
  });
}

export function skipBilling(
  client: ApolloClient<object>
): Promise<SkipBillingMutation> {
  return new Promise((resolve, reject) => {
    const SKIP_BILLING = gql`
      mutation skipBilling {
        skipBilling
      }
    `;
    const result = client.mutate<
      SkipBillingMutation,
      SkipBillingMutationVariables
    >({
      mutation: SKIP_BILLING,
      variables: {},
    });
    result.then(
      (response) => {
        if (response.data) {
          resolve(response.data);
        } else {
          // eslint-disable-next-line @typescript-eslint/prefer-promise-reject-errors -- pre-existing contract: rejects the raw Apollo response
          reject(response);
        }
      },
      (error: Error) => {
        reject(error);
      }
    );
  });
}

function getEmployerRequiredStepsRequest(
  client: ApolloClient<object>
): Promise<GetEmployerRequiredUpdatesQuery["getEmployerRequiredUpdates"]> {
  return new Promise((resolve, reject) => {
    const REQUEST = gql`
      query getEmployerRequiredUpdates {
        getEmployerRequiredUpdates {
          isCompleted
          allSteps
          pendingSteps
        }
      }
    `;
    const result = client.query<
      GetEmployerRequiredUpdatesQuery,
      GetEmployerRequiredUpdatesQueryVariables
    >({
      query: REQUEST,
      variables: {},
    });
    result.then(
      (response) => {
        if (response.data.getEmployerRequiredUpdates) {
          resolve(response.data.getEmployerRequiredUpdates);
        } else {
          // eslint-disable-next-line @typescript-eslint/prefer-promise-reject-errors -- pre-existing contract: rejects the raw Apollo response
          reject(response);
        }
      },
      (error: Error) => {
        reject(error);
      }
    );
  });
}

function updateRequiredOrderFormsRequest(
  client: ApolloClient<object>,
  completedOrderForm: UpdateRequiredOrderFormMutationVariables["completedOrderForm"]
): Promise<UpdateRequiredOrderFormMutation["updateRequiredOrderForm"] | null> {
  return new Promise((resolve, reject) => {
    const REQUEST = gql`
      mutation updateRequiredOrderForm(
        $completedOrderForm: EmployerRequiredStep!
      ) {
        updateRequiredOrderForm(completedOrderForm: $completedOrderForm) {
          isCompleted
          allSteps
          pendingSteps
        }
      }
    `;
    const result = client.mutate<
      UpdateRequiredOrderFormMutation,
      UpdateRequiredOrderFormMutationVariables
    >({
      mutation: REQUEST,
      variables: {
        completedOrderForm,
      },
    });
    result.then(
      (response) => {
        if (response.data?.updateRequiredOrderForm) {
          resolve(response.data?.updateRequiredOrderForm ?? null);
        } else {
          // eslint-disable-next-line @typescript-eslint/prefer-promise-reject-errors -- pre-existing contract: rejects the raw Apollo response
          reject(response);
        }
      },
      (error: Error) => {
        reject(error);
      }
    );
  });
}

function getPayrollIntegrationStatus(
  client: ApolloClient<object>,
  // $companyId is nullable in the schema and the dashboard call omits it
  companyId?: string
): Promise<GetPayrollIntegrationStatusQuery["getPayrollIntegrationStatus"]> {
  return new Promise((resolve, reject) => {
    const REQUEST = gql`
      query getPayrollIntegrationStatus($companyId: ID) {
        getPayrollIntegrationStatus(companyId: $companyId)
      }
    `;
    const result = client.query<
      GetPayrollIntegrationStatusQuery,
      GetPayrollIntegrationStatusQueryVariables
    >({
      query: REQUEST,
      variables: { companyId },
    });
    result.then(
      (response) => {
        if (response.data.getPayrollIntegrationStatus) {
          resolve(response.data.getPayrollIntegrationStatus);
        } else {
          // eslint-disable-next-line @typescript-eslint/prefer-promise-reject-errors -- pre-existing contract: rejects the raw Apollo response
          reject(response);
        }
      },
      (error: Error) => {
        console.error(error);
        reject(error);
      }
    );
  });
}

function companyTermsDate(
  client: ApolloClient<object>
): Promise<CompanyTermsDateQuery["companyTermsDate"]> {
  return new Promise((resolve, reject) => {
    const QUERY = gql`
      query companyTermsDate {
        companyTermsDate {
          createdAt
        }
      }
    `;
    const result = client.query<
      CompanyTermsDateQuery,
      CompanyTermsDateQueryVariables
    >({
      query: QUERY,
      variables: {},
    });
    result.then(
      (response) => {
        if (response.data.companyTermsDate) {
          resolve(response.data.companyTermsDate);
        } else {
          // eslint-disable-next-line @typescript-eslint/prefer-promise-reject-errors -- pre-existing contract: rejects the raw Apollo response
          reject(response);
        }
      },
      (error: Error) => {
        console.error(error);
        reject(error);
      }
    );
  });
}

// The operation name getEmployerOrder is on the backend's authless allowlist, do not change
function getEmployerOrder(
  client: ApolloClient<object>,
  signupCode: GetEmployerOrderQueryVariables["signupCode"]
): Promise<GetEmployerOrderQuery["getEmployerOrder"]> {
  return new Promise((resolve, reject) => {
    const QUERY = gql`
      query getEmployerOrder($signupCode: String!) {
        getEmployerOrder(signupCode: $signupCode) {
          signupCode
          isLinked
        }
      }
    `;
    const result = client.query<
      GetEmployerOrderQuery,
      GetEmployerOrderQueryVariables
    >({
      query: QUERY,
      variables: {
        signupCode,
      },
      fetchPolicy: "network-only",
      // the app client defaults queries to errorPolicy "all", which resolves an invalid code with null data
      errorPolicy: "none",
    });
    result.then(
      (response) => {
        resolve(response.data.getEmployerOrder);
      },
      (error: Error) => {
        reject(error);
      }
    );
  });
}

function getCompanyAgreements(
  client: ApolloClient<object>
): Promise<GetCompanyAgreementsQuery["getCompanyAgreements"]> {
  return new Promise((resolve, reject) => {
    const QUERY = gql`
      query getCompanyAgreements {
        getCompanyAgreements {
          type
          hash
          agreementDate
        }
      }
    `;
    const result = client.query<
      GetCompanyAgreementsQuery,
      GetCompanyAgreementsQueryVariables
    >({
      query: QUERY,
      variables: {},
    });
    result.then(
      (response) => {
        if (response.data.getCompanyAgreements) {
          resolve(response.data.getCompanyAgreements);
        } else {
          // eslint-disable-next-line @typescript-eslint/prefer-promise-reject-errors -- pre-existing contract: rejects the raw Apollo response
          reject(response);
        }
      },
      (error: Error) => {
        console.error(error);
        reject(error);
      }
    );
  });
}

export function getEmployerWalletDetails(
  client: ApolloClient<object>
): Promise<GetEmployerWalletDetailsQuery["getEmployerWalletDetails"]> {
  return new Promise((resolve, reject) => {
    const QUERY = gql`
      query getEmployerWalletDetails {
        getEmployerWalletDetails {
          accountNumber
          routingNumber
          accountType
          createdAt
          updatedAt
          active
        }
      }
    `;
    const result = client.query<
      GetEmployerWalletDetailsQuery,
      GetEmployerWalletDetailsQueryVariables
    >({
      query: QUERY,
      variables: {},
    });
    result.then(
      (response) => {
        if (response.data.getEmployerWalletDetails) {
          resolve(response.data.getEmployerWalletDetails);
        } else {
          // eslint-disable-next-line @typescript-eslint/prefer-promise-reject-errors -- pre-existing contract: rejects the raw Apollo response
          reject(response);
        }
      },
      (error: Error) => {
        reject(error);
      }
    );
  });
}

export function getPayPeriodExamples(
  client: ApolloClient<object>,
  scheduleRequest: GetPayPeriodExamplesQueryVariables["input"]
): Promise<GetPayPeriodExamplesQuery["getPayPeriodExamples"]> {
  return new Promise((resolve, reject) => {
    const REQUEST = gql`
      query getPayPeriodExamples($input: ScheduleRequestInput!) {
        getPayPeriodExamples(input: $input) {
          start
          end
          payrollProcessingDate
          onOrAfterPayrollProcessingDate
          status
          gracePeriodEndDate
          payPeriodStart
          payPeriodEnd
        }
      }
    `;

    const result = client.query<
      GetPayPeriodExamplesQuery,
      GetPayPeriodExamplesQueryVariables
    >({
      query: REQUEST,
      variables: {
        input: scheduleRequest,
      },
    });
    result.then(
      (response) => {
        if (response.data.getPayPeriodExamples) {
          resolve(response.data.getPayPeriodExamples);
        } else {
          // eslint-disable-next-line @typescript-eslint/prefer-promise-reject-errors -- pre-existing contract: rejects the raw Apollo response
          reject(response);
        }
      },
      (error: Error) => {
        reject(error);
      }
    );
  });
}

export function getEmployerContributionAbility(
  client: ApolloClient<object>,
  companyId: string
): Promise<
  | NonNullable<
      NonNullable<
        GetEmployerContributionAbilityQuery["companyById"]
      >["contributionAbility"]
    >
  | object
> {
  return new Promise((resolve, reject) => {
    const request = gql`
      query getEmployerContributionAbility($companyId: ID!) {
        companyById(companyId: $companyId) {
          contributionAbility(companyId: $companyId) {
            securityLimits {
              counts {
                totalPayrolls
                processingPayrolls
                consecutiveAchReturns
                achReturns
                unauthorizedAchReturns
                achReturnPercentage
                unauthorizedAchReturnPercentage
                lookBackDays
              }
            }
            payrollPriorYearCutoffDate
            priorYearCutoffDate
            isWithinPayrollPriorYearContributionWindow
            isWithinPriorYearContributionWindow
            currentYear {
              taxYear
              amountRemaining
              canProcessPayroll
            }
            priorYear {
              taxYear
              amountRemaining
              canProcessPayroll
            }
            allYears {
              taxYear
              amountRemaining
              canProcessPayroll
            }
          }
        }
      }
    `;
    const result = client.query<
      GetEmployerContributionAbilityQuery,
      GetEmployerContributionAbilityQueryVariables
    >({
      query: request,
      variables: {
        companyId,
      },
    });
    result.then(
      (data) => {
        resolve(get(data, "data.companyById.contributionAbility", {}));
      },
      (error: Error) => {
        reject(error);
      }
    );
  });
}

function acknowledgeRecurringDebits(
  client: ApolloClient<object>
): Promise<
  AcknowledgeRecurringDebitsMutation["acknowledgeRecurringDebits"] | null
> {
  return new Promise((resolve, reject) => {
    const REQUEST = gql`
      mutation acknowledgeRecurringDebits {
        acknowledgeRecurringDebits
      }
    `;
    const result = client.mutate<
      AcknowledgeRecurringDebitsMutation,
      AcknowledgeRecurringDebitsMutationVariables
    >({
      mutation: REQUEST,
      variables: {},
    });
    result.then(
      (response) => {
        resolve(response.data?.acknowledgeRecurringDebits ?? null);
      },
      (error: Error) => {
        reject(error);
      }
    );
  });
}

function getRecurringDebitsAcknowledgment(
  client: ApolloClient<object>
): Promise<GetRecurringDebitsAcknowledgmentQuery["company"] | null> {
  return new Promise((resolve, reject) => {
    const REQUEST = gql`
      query getRecurringDebitsAcknowledgment {
        company {
          id
          hasAcknowledgedRecurringDebits
        }
      }
    `;
    const result = client.query<
      GetRecurringDebitsAcknowledgmentQuery,
      GetRecurringDebitsAcknowledgmentQueryVariables
    >({
      query: REQUEST,
      variables: {},
      fetchPolicy: "network-only",
      // the app client defaults queries to errorPolicy "all", which would hide a failed check as null
      errorPolicy: "none",
    });
    result.then(
      (response) => {
        resolve(response.data?.company ?? null);
      },
      (error: Error) => {
        reject(error);
      }
    );
  });
}

function activatePayrollIntegrationSetup(
  client: ApolloClient<object>
): Promise<
  ActivatePayrollIntegrationMutation["activatePayrollIntegration"] | null
> {
  return new Promise((resolve, reject) => {
    const REQUEST = gql`
      mutation activatePayrollIntegration {
        activatePayrollIntegration
      }
    `;
    const result = client.mutate<
      ActivatePayrollIntegrationMutation,
      ActivatePayrollIntegrationMutationVariables
    >({
      mutation: REQUEST,
      variables: {},
    });
    result.then(
      (response) => {
        if (response.data?.activatePayrollIntegration) {
          resolve(response.data?.activatePayrollIntegration ?? null);
        } else {
          // eslint-disable-next-line @typescript-eslint/prefer-promise-reject-errors -- pre-existing contract: rejects the raw Apollo response
          reject(response);
        }
      },
      (error: Error) => {
        reject(error);
      }
    );
  });
}

function submitUkgConfig(
  client: ApolloClient<object>,
  { clientId, secret, companyCode }: SubmitUkgConfigMutationVariables
): Promise<SubmitUkgConfigMutation["submitUkgConfig"] | null> {
  return new Promise((resolve, reject) => {
    const REQUEST = gql`
      mutation submitUkgConfig(
        $clientId: String!
        $secret: String!
        $companyCode: String!
      ) {
        submitUkgConfig(
          clientId: $clientId
          secret: $secret
          companyCode: $companyCode
        )
      }
    `;
    const result = client.mutate<
      SubmitUkgConfigMutation,
      SubmitUkgConfigMutationVariables
    >({
      mutation: REQUEST,
      variables: { clientId, secret, companyCode },
    });
    result.then(
      (response) => {
        if (response.data?.submitUkgConfig) {
          resolve(response.data?.submitUkgConfig ?? null);
        } else {
          // eslint-disable-next-line @typescript-eslint/prefer-promise-reject-errors -- pre-existing contract: rejects the raw Apollo response
          reject(response);
        }
      },
      (error: Error) => {
        reject(error);
      }
    );
  });
}

function getUkgConfig(
  client: ApolloClient<object>
): Promise<GetUkgConfigQuery["getUkgConfig"] | null> {
  return new Promise((resolve, reject) => {
    const REQUEST = gql`
      query getUkgConfig {
        getUkgConfig
      }
    `;
    const result = client.query<GetUkgConfigQuery, GetUkgConfigQueryVariables>({
      query: REQUEST,
      variables: {},
      fetchPolicy: "network-only",
    });
    result.then(
      (response) => {
        if (response.data.getUkgConfig) {
          resolve(response.data.getUkgConfig);
        } else {
          resolve(null);
        }
      },
      (error: Error) => {
        reject(error);
      }
    );
  });
}

export const employerService = {
  register,
  submitEmployerAgents,
  getCompany,
  findEmployerByUser,
  approvePendingLinkedEmployees,
  getAllEmployerAccounts,
  rejectPendingLinkedEmployees,
  getAllEmployerGroups,
  addEmployerGroup,
  getPendingLinkedEmployees,
  updateEmployerGroup,
  deleteEmployerGroup,
  inviteEmployees,
  resendInvitation,
  getEmployeeInvites,
  getTakeRateEmployeesForCompany,
  getLinkedEmployees,
  processPayroll,
  getUserProfilesForGroup,
  approveContributionChanges,
  rejectContributionChanges,
  deleteEmployerLinks,
  getAdministrators,
  resendAdministratorInvite,
  inviteAdministrator,
  lockAdministrator,
  revokeInvitation,
  unlockAdministrator,
  uploadEmployeesDocumentRequest,
  getEmployeeDocumentUploadJobsRequest,
  resendInvitations,
  getEmployerGroup,
  getPaginatedEmployees,
  setEmployerSession,
  getLinkedCompanies,
  getPaginatedInvites,
  changeAdministratorRole,
  getBeneficialOwners,
  validateSelfServe,
  getSetupIntent,
  getPaymentRequirement,
  createSetupIntent,
  setInvoicePaymentMethod,
  getBillingPortalURL,
  getProgressiveOnboarding,
  updateProgressiveOnboarding,
  uploadRoster,
  stageEmployeeInvites,
  getEmployeeRoster,
  resyncEmployeeRoster,
  processRoster,
  approveRoster,
  referCompany,
  getPayrollGroupContributionAbilityRequest,
  currentPayrolls,
  skipBilling,
  hydrateDashboard,
  getEmployerRequiredStepsRequest,
  updateRequiredOrderFormsRequest,
  getPayrollIntegrationStatus,
  companyTermsDate,
  getEmployerOrder,
  getCompanyAgreements,
  getEmployerWalletDetails,
  getPayPeriodExamples,
  getEmployerContributionAbility,
  activatePayrollIntegrationSetup,
  acknowledgeRecurringDebits,
  getRecurringDebitsAcknowledgment,
  submitUkgConfig,
  getUkgConfig,
};
